import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, InfoWindow } from "@react-google-maps/api";
import { Alert, Box, CircularProgress, IconButton } from "@mui/material";
import { CustomMultiSelect } from "@/components/common/inputFields/customMultiSelect";
import FullscreenIcon from "@mui/icons-material/Fullscreen";
import CloseIcon from "@mui/icons-material/Close";
import { CustomIconButton } from "@/components/common/customIconButton";
import { MapMarker } from "@/components/common/maps/MapMarkers";
import { MapCustomControls } from "@/components/common/maps/MapControls";
import { getMapLoadingStates } from "@/context/MapsLoaderContext";
import { DealCompMapPopUp } from "@/components/deal/DealCompMapPopUp";
import { DealCompDuplicateMapPopUp } from "@/components/deal/DealCompDuplicateMapPopUp";
import "../../assets/css/GoogleMaps.css";
import {
  buildSVGIconSimilarAssetsSVGIcon,
  buildSVGIconSimilarAssetsWithDuplicateCount,
  COMPS_PIN_DISPLAY_SIZE,
} from "@/components/common/maps/AssetMapHelperFunctions";
import type { MapCoordinate } from "../common/maps/GoogleMap";
import { MapPopUp } from "@/components/common/maps/MapPopUp";

/** Subset of comp row fields used for the map (from lease / sales / loan tabs). */
export type DealCompMapEntry = {
  lat: number | null;
  lng: number | null;
  location: string | null;
  /** Lease tab: city for map popup and marker title fallback. */
  city: string | null;
  lender: string | null;
  borrower: string | null;
  loan_amount: number | null;
  /** Sales tab: shown in the map info window when `compMapPopupVariant` is `sales`. */
  buyer: string | null;
  vendor: string | null;
  currency: string | null;
  area_type: string | null;
  /** Lease tab: `asset_type` from API, else `area_type` (e.g. SQM). */
  asset_type: string | null;
  yield_pct: number | null;
  sales_price: number | null;
  no_of_keys_or_rooms: number | null;
  /** Sales: price per unit area. */
  price_per_area?: number | null;
  /** Sales: price per key or room. */
  price_per_key_room?: number | null;
  tenant_name: string | null;
  asset_name: string | null;
  lease_term: string | null;
  total_rent: number | null;
  /** Legacy row field; map plots all valid coordinates regardless of `checked`. */
  checked?: boolean;
  source: string | null;
  /** Lease: link target for the source label in the map card; not shown as its own row. */
  web_url?: string | null;
  deal_name?: string | null;
  rent_per_area?: number | null;
  age_refurb_date?: string | null;
};

export type DealCompComparisonMapProps = {
  /** Active tab’s comps: every entry with finite `lat`/`lng` gets a marker. */
  compMapEntries: readonly DealCompMapEntry[];
  zoom?: number;
  height?: number | string;
  width?: number | string;
  /** When true, numbered asset-style pins (1…n) like `GoogleMap` main markers. */
  useAssetNumbers?: boolean;
  /** Pin image when `useAssetNumbers` is false (passed as `MapMarker` `icon`). */
  defaultIcon?: string;
  mapOptions?: google.maps.MapOptions;
  onMarkerClick?: (entry: DealCompMapEntry, index: number) => void;
  draggable?: boolean;
  onMarkerDragEnd?: (event: google.maps.MapMouseEvent) => void;
  /** When this value changes (e.g. comps tab index), the open marker selection is cleared. */
  selectionResetKey?: number | string;
  setMapFullscreenOpen?: (open: boolean) => void;
  mapFullscreenOpen?: boolean;
  hideFullscreenButton?: boolean;
  /** When true, the source multi-select has no outline border (hover/focus), no wrapper padding, and no padding on the control / open menu list. */
  hideSourceFilterSelectBorder?: boolean;
  sourceNames: string[];
  /** Controls which fields the marker info window shows (matches active comps tab). */
  compMapPopupVariant?: "lease" | "loan" | "sales";
  coordinates?: MapCoordinate[];
  center: { lat: number; lng: number };
  /**
   * Index into `compMapEntries` of the comp to focus (pans the map and opens its info window).
   * This is the comp's position in the data array, not the grid's displayed (sorted) row index.
   */
  focusedCompEntryIndex?: number | null;
  /** Incremented on each grid row click so re-clicking the same row still focuses the map. */
  compMapFocusSeq?: number;
};

/**
 * Minimal map for comps: loads the Maps JS API, centers on the marker set, and renders one `MapMarker` per coordinate.
 */
function compEntryTitle(
  entry: DealCompMapEntry,
  index: number,
  popupVariant: "lease" | "loan" | "sales",
): string {
  if (entry.location?.trim()) return entry.location.trim();
  if (popupVariant === "lease") {
    const parts = [entry.asset_name, entry.city, entry.tenant_name].filter(
      Boolean,
    );
    if (parts.length) return parts.join(" · ");
  }
  if (popupVariant === "sales") {
    const parts = [entry.buyer, entry.vendor].filter(Boolean);
    if (parts.length) return parts.join(" · ");
  }
  const parts = [entry.lender, entry.borrower].filter(Boolean);
  if (parts.length) return parts.join(" · ");
  return `Location ${index + 1}`;
}

export function DealCompComparisonMap({
  compMapEntries,
  zoom,
  height = 360,
  width = "100%",
  useAssetNumbers = false,
  defaultIcon = "/map-marker.svg",
  mapOptions = {},
  onMarkerClick,
  draggable = false,
  onMarkerDragEnd,
  selectionResetKey,
  setMapFullscreenOpen,
  mapFullscreenOpen,
  hideFullscreenButton = false,
  sourceNames,
  compMapPopupVariant = "loan",
  coordinates = [],
  center,
  focusedCompEntryIndex = null,
  compMapFocusSeq = 0,
}: DealCompComparisonMapProps) {
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY || "";

  const { isLoaded, loadError } = getMapLoadingStates();

  const [selectedMarker, setSelectedMarker] = useState<MapCoordinate | null>(
    null,
  );

  /** Empty array = show every comp source on the map. */
  const [selectedSourceFilter, setSelectedSourceFilter] = useState<string[]>(
    [],
  );

  const [infoWindowOpenAsset, setInfoWindowOpenAsset] = useState(false);

  const baseStyles = useMemo(() => {
    const styles: google.maps.MapTypeStyle[] = [
      { featureType: "poi", stylers: [{ visibility: "on" }] },
      { featureType: "transit", stylers: [{ visibility: "off" }] },
    ];

    return styles;
  }, []);

  const defaultOptions = useMemo(
    () => ({
      zoomControl: false,
      fullscreenControl: false,
      streetViewControl: true,
      panControl: false, // Disable pan control (arrow buttons)
      rotateControl: false, // Disable rotate control
      scaleControl: false, // Disable scale control
      overviewMapControl: false, // Disable overview map control
      cameraControl: false, // Disable camera control
      streetViewControlOptions: {
        position:
          (typeof window !== "undefined" &&
            window.google?.maps?.ControlPosition?.RIGHT_BOTTOM) ||
          9,
      },
      gestureHandling: "cooperative",
      mapTypeControl: false,
      styles: mapOptions.styles
        ? [...baseStyles, ...mapOptions.styles]
        : baseStyles,
      ...mapOptions,
    }),
    [mapOptions, baseStyles],
  );

  const sourceDropdownOptions = useMemo(
    () =>
      sourceNames.map((name, i) => ({
        id: name,
        label: name,
        pinIndex: i + 1,
      })),
    [sourceNames],
  );

  const { plottableMarkers, duplicateMarkerGroups } = useMemo(() => {
    const coordKey = (lat: number, lng: number) => `${lat},${lng}`;

    // One object per comp row that has usable map coordinates (keep original row index)
    const rowsWithCoords = compMapEntries
      .map((entry, index) => ({ entry, index }))
      .filter(({ entry }) => {
        const coordsOk =
          typeof entry.lat === "number" &&
          typeof entry.lng === "number" &&
          Number.isFinite(entry.lat) &&
          Number.isFinite(entry.lng);
        if (!coordsOk) return false;
        if (selectedSourceFilter.length === 0) return true;
        return selectedSourceFilter.includes(entry.source || "");
      })
      .map(({ entry, index }) => ({
        entry,
        index,
        lat: entry.lat as number,
        lng: entry.lng as number,
      }));

    // Distinct positions, in the order we first see them
    const distinctKeys: string[] = [];
    rowsWithCoords.forEach((row) => {
      const key = coordKey(row.lat, row.lng);
      if (!distinctKeys.includes(key)) {
        distinctKeys.push(key);
      }
    });

    const plottableMarkers: {
      entry: DealCompMapEntry;
      index: number;
      lat: number;
      lng: number;
      source: string;
    }[] = [];

    const duplicateMarkerGroups: {
      allEntries: { entry: DealCompMapEntry; index: number }[];
      lat: number;
      lng: number;
      source: string;
    }[] = [];

    distinctKeys.forEach((key) => {
      const sameSpot = rowsWithCoords.filter(
        (row) => coordKey(row.lat, row.lng) === key,
      );

      if (sameSpot.length === 1) {
        const row = sameSpot[0];
        plottableMarkers.push({
          entry: row.entry,
          index: row.index,
          lat: row.lat,
          lng: row.lng,
          source: row.entry.source || "",
        });
        return;
      }

      const first = sameSpot[0];
      duplicateMarkerGroups.push({
        allEntries: sameSpot.map((row) => ({
          entry: row.entry,
          index: row.index,
        })),
        lat: first.lat,
        lng: first.lng,
        source: first.entry.source || "",
      });
    });

    return { plottableMarkers, duplicateMarkerGroups };
  }, [compMapEntries, selectedSourceFilter]);

  const mapRef = useRef<google.maps.Map | null>(null);
  const [mapType, setMapType] = useState<string>("roadmap");

  const [selectedPlottable, setSelectedPlottable] = useState<{
    entry: DealCompMapEntry;
    index: number;
    lat: number;
    lng: number;
  } | null>(null);
  const [infoWindowOpen, setInfoWindowOpen] = useState(false);
  const [selectedDuplicate, setSelectedDuplicate] = useState<{
    allEntries: { entry: DealCompMapEntry; index: number }[];
    lat: number;
    lng: number;
  } | null>(null);
  const [infoWindowOpenDuplicate, setInfoWindowOpenDuplicate] = useState(false);
  const [infoWindowKey, setInfoWindowKey] = useState(0);

  /** Padding (px) around markers so pins aren’t flush to the map edge. */
  const FIT_BOUNDS_PADDING = 48;

  const buildSVGIconSimilarAssets = useCallback(
    (index: number) => {
      if (!isLoaded || typeof window === "undefined" || !window.google?.maps)
        return undefined;

      return buildSVGIconSimilarAssetsSVGIcon(index, COMPS_PIN_DISPLAY_SIZE);
    },
    [isLoaded],
  );

  const buildDuplicateGroupPinIcon = useCallback(
    (colorIndex: number, duplicateCount: number) => {
      if (!isLoaded || typeof window === "undefined" || !window.google?.maps)
        return undefined;

      return buildSVGIconSimilarAssetsWithDuplicateCount(
        colorIndex,
        duplicateCount,
        COMPS_PIN_DISPLAY_SIZE,
      );
    },
    [isLoaded],
  );

  const fitMapToMarkers = useCallback(
    (map: google.maps.Map) => {
      if (zoom !== undefined && zoom !== null) {
        map.setCenter(center);
        map.setZoom(zoom);
        return;
      }

      const pts = [...duplicateMarkerGroups, ...plottableMarkers];
      if (pts.length === 0) {
        const assetPts = coordinates.filter(
          (c) =>
            Number.isFinite(c.lat) &&
            Number.isFinite(c.lng),
        );
        if (assetPts.length > 0) {
          if (assetPts.length === 1) {
            map.setCenter({ lat: assetPts[0].lat, lng: assetPts[0].lng });
            map.setZoom(13);
          } else {
            const bounds = new google.maps.LatLngBounds();
            assetPts.forEach((c) => {
              bounds.extend(new google.maps.LatLng(c.lat, c.lng));
            });
            map.fitBounds(bounds, FIT_BOUNDS_PADDING);
          }
          return;
        }
        map.setCenter(center);
        map.setZoom(zoom ?? 6);
        return;
      }
      if (pts.length === 1) {
        map.setCenter({ lat: pts[0].lat, lng: pts[0].lng });
        // One zoom level out vs 14 ≈ 2× geographic span (avoids feeling “too tight”).
        map.setZoom(13);
        return;
      }

      const bounds = new google.maps.LatLngBounds();
      pts.forEach(({ lat, lng }) => {
        bounds.extend(new google.maps.LatLng(lat, lng));
      });
      map.fitBounds(bounds, FIT_BOUNDS_PADDING);
    },
    [zoom, center, coordinates, plottableMarkers, duplicateMarkerGroups],
  );

  const focusCompAtEntryIndex = useCallback(
    (entryIndex: number) => {
      const entry = compMapEntries[entryIndex];
      if (
        !entry ||
        typeof entry.lat !== "number" ||
        typeof entry.lng !== "number" ||
        !Number.isFinite(entry.lat) ||
        !Number.isFinite(entry.lng)
      ) {
        return;
      }

      const lat = entry.lat;
      const lng = entry.lng;
      setInfoWindowKey((k) => k + 1);

      const plottable = plottableMarkers.find((m) => m.index === entryIndex);
      if (plottable) {
        setSelectedDuplicate(null);
        setInfoWindowOpenDuplicate(false);
        setSelectedPlottable({
          entry: plottable.entry,
          index: plottable.index,
          lat: plottable.lat,
          lng: plottable.lng,
        });
        setInfoWindowOpen(true);
        mapRef.current?.panTo({ lat, lng });
        onMarkerClick?.(plottable.entry, plottable.index);
        return;
      }

      const duplicate = duplicateMarkerGroups.find((g) =>
        g.allEntries.some((e) => e.index === entryIndex),
      );
      if (duplicate) {
        setSelectedPlottable(null);
        setInfoWindowOpen(false);
        setSelectedDuplicate({
          allEntries: duplicate.allEntries,
          lat: duplicate.lat,
          lng: duplicate.lng,
        });
        setInfoWindowOpenDuplicate(true);
        mapRef.current?.panTo({ lat, lng });
      }
    },
    [
      compMapEntries,
      plottableMarkers,
      duplicateMarkerGroups,
      onMarkerClick,
    ],
  );

  useEffect(() => {
    if (compMapFocusSeq === 0) return;
    if (focusedCompEntryIndex == null || focusedCompEntryIndex < 0) return;
    focusCompAtEntryIndex(focusedCompEntryIndex);
  }, [compMapFocusSeq, focusedCompEntryIndex, focusCompAtEntryIndex]);

  const onMapLoad = useCallback(
    (map: google.maps.Map) => {
      mapRef.current = map;
      fitMapToMarkers(map);
    },
    [fitMapToMarkers],
  );

  const onMapUnmount = useCallback(() => {
    mapRef.current = null;
  }, []);

  useEffect(() => {
    const m = mapRef.current;
    if (m) fitMapToMarkers(m);
  }, [fitMapToMarkers]);

  useEffect(() => {
    setSelectedPlottable(null);
    setInfoWindowOpen(false);
    setSelectedDuplicate(null);
    setInfoWindowOpenDuplicate(false);
    setSelectedSourceFilter([]);
  }, [selectionResetKey]);

  const handleInfoWindowClose = useCallback(() => {
    setSelectedMarker(null);
    setInfoWindowOpenAsset(false);
  }, []);

  const handleInfoWindowCloseDealComp = useCallback(() => {
    setSelectedPlottable(null);
    setInfoWindowOpen(false);
  }, []);

  const handleInfoWindowCloseDuplicate = useCallback(() => {
    setSelectedDuplicate(null);
    setInfoWindowOpenDuplicate(false);
  }, []);

  const handleMarkerClick = useCallback(
    (entry: DealCompMapEntry, index: number, lat: number, lng: number) => {
      setInfoWindowKey((k) => k + 1);

      const isSame =
        selectedPlottable?.index === index &&
        selectedPlottable.lat === lat &&
        selectedPlottable.lng === lng;

      if (isSame) {
        setSelectedPlottable(null);
        setInfoWindowOpen(false);
      } else {
        setSelectedDuplicate(null);
        setInfoWindowOpenDuplicate(false);
        setSelectedPlottable({ entry, index, lat, lng });
        setInfoWindowOpen(true);
        mapRef.current?.panTo({ lat, lng });
      }

      onMarkerClick?.(entry, index);
    },
    [onMarkerClick, selectedPlottable],
  );

  const handleDuplicateMarkerClick = useCallback(
    (
      allEntries: { entry: DealCompMapEntry; index: number }[],
      lat: number,
      lng: number,
    ) => {
      setInfoWindowKey((k) => k + 1);

      const isSame =
        selectedDuplicate?.lat === lat &&
        selectedDuplicate?.lng === lng &&
        infoWindowOpenDuplicate;

      if (isSame) {
        setSelectedDuplicate(null);
        setInfoWindowOpenDuplicate(false);
      } else {
        setSelectedPlottable(null);
        setInfoWindowOpen(false);
        setSelectedDuplicate({ allEntries, lat, lng });
        setInfoWindowOpenDuplicate(true);
        mapRef.current?.panTo({ lat, lng });
      }
    },
    [selectedDuplicate, infoWindowOpenDuplicate],
  );

  const handleMarkerClickAsset = useCallback(
    (coordinate: MapCoordinate) => {
      if (selectedMarker?.id === coordinate.id) {
        setSelectedMarker(null);
        setInfoWindowOpenAsset(false);
      } else {
        setSelectedMarker(coordinate);
        setInfoWindowOpenAsset(true);
        mapRef.current?.panTo({ lat: coordinate.lat, lng: coordinate.lng });
      }
    },
    [selectedMarker],
  );

  const containerStyle = useMemo(
    () => ({
      width: typeof width === "number" ? `${width}px` : width,
      height: typeof height === "number" ? `${height}px` : height,
    }),
    [width, height],
  );

  if (!apiKey) {
    return (
      <Box
        sx={{
          height: containerStyle.height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Alert severity="error" sx={{ maxWidth: 400 }}>
          Google Maps API key not found. Add VITE_GOOGLE_MAPS_API_KEY to your
          environment variables.
        </Alert>
      </Box>
    );
  }

  if (loadError) {
    return (
      <Box
        sx={{
          height: containerStyle.height,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          p: 2,
        }}
      >
        <Alert severity="error">Failed to load Google Maps.</Alert>
      </Box>
    );
  }

  if (!isLoaded) {
    return (
      <Box
        sx={{
          height: containerStyle.height,
          width: containerStyle.width,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box
      className="google-map-container deal-comp-comparison-map"
      sx={{
        position: "relative",
        // Override global GoogleMaps.css infowindow caps for comps popup only.
        "& .gm-style-iw-c": {
          maxHeight: "2000px !important",
        },
        "& .gm-style-iw-d": {
          maxHeight: "2000px !important",
          overflow: "auto !important",
        },
      }}
      style={containerStyle}
    >
      {sourceNames.length > 0 ? (
        <Box
          sx={{
            position: "absolute",
            top: 12,
            left: 12,
            zIndex: 3,
            minWidth: 200,
            maxWidth: 280,
            bgcolor: "rgba(255,255,255,0.92)",
            borderRadius: 1,
            boxShadow: 2,
            p: 0,
            pointerEvents: "auto",
          }}
        >
          <CustomMultiSelect
            value={selectedSourceFilter}
            onChange={(e) =>
              setSelectedSourceFilter(e.target.value as string[])
            }
            options={sourceDropdownOptions}
            placeholder="All sources"
            labelKey="label"
            pinIndexKey="pinIndex"
            minWidth="200px"
            heightOverride="36px"
            hideBorder={false}
          />
        </Box>
      ) : null}

      {coordinates.length > 0 && (
        <Box
          sx={{
            position: "absolute",
            bottom: 12,
            left: 12,
            zIndex: 3,
            backgroundColor: "rgba(255,255,255,0.92)",
            borderRadius: 1,
            boxShadow: 3,
            p: 1.5,
            minWidth: 180,
            maxWidth: 260,
            maxHeight: "66.666%",
            display: "flex",
            flexDirection: "column",
            pointerEvents: "auto",
          }}
        >
          <Box
            sx={{
              fontWeight: 700,
              fontSize: 12,
              mb: 0.75,
              color: "#333",
              flexShrink: 0,
            }}
          >
            Assets
          </Box>
          <Box
            sx={{
              display: "flex",
              flexDirection: "column",
              gap: 0.5,
              overflowY: "auto",
              minHeight: 0,
              flex: 1,
            }}
          >
            {coordinates.map((entry, index) => (
              <Box
                key={entry.id}
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  fontSize: 12,
                  color: "#1f1f1f",
                  whiteSpace: "nowrap",
                  flexShrink: 0,
                }}
              >
                <Box
                  sx={{
                    minWidth: 20,
                    height: 20,
                    borderRadius: "50%",
                    backgroundColor: "#800000",
                    color: "#fff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 11,
                    fontWeight: 700,
                  }}
                >
                  {index + 1}
                </Box>
                <Box
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    flex: 1,
                  }}
                  title={entry.title}
                >
                  {entry.title}
                </Box>
              </Box>
            ))}
          </Box>
        </Box>
      )}

      <GoogleMap
        mapContainerStyle={{ width: "100%", height: "100%" }}
        center={center}
        zoom={zoom ?? 6}
        onLoad={onMapLoad}
        onUnmount={onMapUnmount}
        options={defaultOptions}
      >
        {plottableMarkers.map(({ entry, index, lat, lng, source }) => {
          const sourceIdx = sourceNames.indexOf(source);
          const pinIndex = sourceIdx >= 0 ? sourceIdx + 1 : index + 1;
          return (
            <MapMarker
              key={`${lat}-${lng}-${index}`}
              latitude={lat}
              longitude={lng}
              title={compEntryTitle(entry, index, compMapPopupVariant)}
              {...(useAssetNumbers
                ? { assetIndex: index + 1, assetId: index }
                : { icon: defaultIcon })}
              zIndex={99999}
              onMarkerClick={() => handleMarkerClick(entry, index, lat, lng)}
              draggable={draggable}
              onDragEnd={onMarkerDragEnd}
              icon={buildSVGIconSimilarAssets(pinIndex)}
            />
          );
        })}
        {duplicateMarkerGroups.map(({ allEntries, lat, lng, source }) => {
          const sourceIdx = sourceNames.indexOf(source);
          const pinIndex =
            sourceIdx >= 0 ? sourceIdx + 1 : allEntries[0].index + 1;
          return (
            <MapMarker
              key={`dup-${lat}-${lng}-${allEntries.map((e) => e.index).join("-")}`}
              latitude={lat}
              longitude={lng}
              title={allEntries
                .map(({ entry }) => entry.location?.trim() || "")
                .filter(Boolean)
                .join(", ")}
              zIndex={99999}
              onMarkerClick={() =>
                handleDuplicateMarkerClick(allEntries, lat, lng)
              }
              draggable={draggable}
              onDragEnd={onMarkerDragEnd}
              icon={buildDuplicateGroupPinIcon(pinIndex, allEntries.length)}
            />
          );
        })}

        {/* Info Window */}
        {selectedMarker && infoWindowOpenAsset && (
          <InfoWindow
            key={infoWindowKey}
            position={{ lat: selectedMarker.lat, lng: selectedMarker.lng }}
          >
            <MapPopUp
              showCloseButton
              onClose={handleInfoWindowClose}
              data={{
                name: selectedMarker.title,
                category: selectedMarker.category,
                sector: selectedMarker.sector,
                dealName: selectedMarker.dealName,
                dealId: selectedMarker.dealId,
                acquisitionPrice: selectedMarker.acquisitionPrice,
                valuationAmount: selectedMarker.valuationAmount,
                totalArea: selectedMarker.totalArea,
                areaType: selectedMarker.areaType,
                currency: selectedMarker.currency,
                distance: (selectedMarker as any).distance,
                travelTime: (selectedMarker as any).travelTime,
              }}
            />
          </InfoWindow>
        )}

        {coordinates.map((coordinate, index) => (
          <MapMarker
            key={`${coordinate.lat}-${coordinate.lng}-${index}`}
            latitude={coordinate.lat}
            longitude={coordinate.lng}
            title={coordinate.title || `Location ${index + 1}`}
            {...(useAssetNumbers
              ? { assetIndex: index + 1, assetId: coordinate.id }
              : {})}
            zIndex={99999}
            onMarkerClick={() => handleMarkerClickAsset(coordinate)}
          />
        ))}

        <MapCustomControls
          mapType={mapType}
          onChangeMapType={(type: string) => {
            setMapType(type);
            const map = mapRef.current;
            if (map) {
              map.setMapTypeId(type as google.maps.MapTypeId);
            }
          }}
          onZoomIn={() => {
            const map = mapRef.current;
            if (map) {
              const currentZoom = map.getZoom();
              if (currentZoom !== undefined && currentZoom !== null) {
                map.setZoom(currentZoom + 1);
              }
            }
          }}
          onZoomOut={() => {
            const map = mapRef.current;
            if (map) {
              const currentZoom = map.getZoom();
              if (currentZoom !== undefined && currentZoom !== null) {
                map.setZoom(currentZoom - 1);
              }
            }
          }}
        />

        {selectedPlottable && infoWindowOpen && (
          <InfoWindow
            key={infoWindowKey}
            position={{
              lat: selectedPlottable.lat,
              lng: selectedPlottable.lng,
            }}
            onCloseClick={handleInfoWindowCloseDealComp}
          >
            <DealCompMapPopUp
              {...(compMapPopupVariant === "sales"
                ? {
                    variant: "sales" as const,
                    buyer: selectedPlottable.entry.buyer,
                    vendor: selectedPlottable.entry.vendor,
                    currency: selectedPlottable.entry.currency,
                    location: selectedPlottable.entry.location,
                    area_type: selectedPlottable.entry.area_type,
                    yield_pct: selectedPlottable.entry.yield_pct,
                    sales_price: selectedPlottable.entry.sales_price,
                    price_per_area:
                      selectedPlottable.entry.price_per_area ?? null,
                    no_of_keys_or_rooms:
                      selectedPlottable.entry.no_of_keys_or_rooms,
                    price_per_key_room:
                      selectedPlottable.entry.price_per_key_room ?? null,
                    age_refurb_date:
                      selectedPlottable.entry.age_refurb_date ?? null,
                  }
                : compMapPopupVariant === "lease"
                  ? {
                      variant: "lease" as const,
                      web_url: selectedPlottable.entry.web_url ?? null,
                      city: selectedPlottable.entry.city,
                      source: selectedPlottable.entry.source,
                      deal_name: selectedPlottable.entry.deal_name ?? null,
                      currency: selectedPlottable.entry.currency,
                      asset_type: selectedPlottable.entry.asset_type,
                      asset_name: selectedPlottable.entry.asset_name,
                      lease_term: selectedPlottable.entry.lease_term,
                      total_rent: selectedPlottable.entry.total_rent,
                      rent_per_area:
                        selectedPlottable.entry.rent_per_area ?? null,
                      tenant_name: selectedPlottable.entry.tenant_name,
                      age_refurb_date:
                        selectedPlottable.entry.age_refurb_date ?? null,
                    }
                  : {
                      variant: "loan" as const,
                      location: selectedPlottable.entry.location,
                      lender: selectedPlottable.entry.lender,
                      borrower: selectedPlottable.entry.borrower,
                      loan_amount: selectedPlottable.entry.loan_amount,
                    })}
              firstRowTrailing={
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Close popup"
                  disableRipple
                  disableFocusRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInfoWindowCloseDealComp();
                  }}
                  sx={{
                    color: "text.secondary",
                    bgcolor: "rgba(255,255,255,0.92)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    outline: "none",
                    boxShadow: "none",
                    "&:focus": { outline: "none" },
                    "&.Mui-focusVisible": {
                      bgcolor: "rgba(255,255,255,0.92)",
                    },
                    padding: 0,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              }
            />
          </InfoWindow>
        )}

        {selectedDuplicate && infoWindowOpenDuplicate && (
          <InfoWindow
            key={`dup-${infoWindowKey}`}
            position={{
              lat: selectedDuplicate.lat,
              lng: selectedDuplicate.lng,
            }}
            onCloseClick={handleInfoWindowCloseDuplicate}
          >
            <DealCompDuplicateMapPopUp
              allEntries={selectedDuplicate.allEntries}
              compMapPopupVariant={compMapPopupVariant}
              onSelectEntry={(entry, index) => {
                onMarkerClick?.(entry, index);
              }}
              firstRowTrailing={
                <IconButton
                  type="button"
                  size="small"
                  aria-label="Close popup"
                  disableRipple
                  disableFocusRipple
                  onClick={(e) => {
                    e.stopPropagation();
                    handleInfoWindowCloseDuplicate();
                  }}
                  sx={{
                    color: "text.secondary",
                    bgcolor: "rgba(255,255,255,0.92)",
                    "&:hover": { bgcolor: "rgba(255,255,255,1)" },
                    outline: "none",
                    boxShadow: "none",
                    "&:focus": { outline: "none" },
                    "&.Mui-focusVisible": {
                      bgcolor: "rgba(255,255,255,0.92)",
                    },
                    padding: 0,
                  }}
                >
                  <CloseIcon sx={{ fontSize: 18 }} />
                </IconButton>
              }
            />
          </InfoWindow>
        )}
      </GoogleMap>

      {!hideFullscreenButton && setMapFullscreenOpen && (
        <Box
          sx={{
            position: "absolute",
            top: 5,
            right: mapFullscreenOpen ? 10 : 0,
            zIndex: 2,
            display: "flex",
            gap: 1,
            pointerEvents: "auto",
          }}
        >
          <CustomIconButton
            icon={mapFullscreenOpen ? <CloseIcon /> : <FullscreenIcon />}
            onClick={() => setMapFullscreenOpen(!mapFullscreenOpen)}
            data-position="left"
          />
        </Box>
      )}
    </Box>
  );
}

export default DealCompComparisonMap;
